// The money rules of the checkout. They mirror the server (astro-backend/src/services/settings.service.js
// and order.service.js), which is the authority: what is shown here is what the server will charge.

import { round2 } from "./orderPricing";

const num = (v) => Number(v) || 0;

// Shipping is free once the goods reach the threshold (judged before any discount); COD adds its fee.
export const computeCharges = (settings, subtotal, paymentMethod) => {
  const free = settings.freeShippingAbove != null && subtotal >= settings.freeShippingAbove;
  return {
    shippingFee: free ? 0 : num(settings.shippingFee),
    codFee: paymentMethod === "cod" ? num(settings.codFee) : 0,
  };
};

// Cash discount for paying online (never COD), on the goods after any coupon; it can never exceed them.
export const computePrepaidDiscount = (settings, base, paymentMethod) => {
  if (paymentMethod !== "payu") return 0;
  const type = settings.prepaidDiscountType;
  const value = num(settings.prepaidDiscountValue);
  if (!["flat", "percent"].includes(type) || value <= 0 || base <= 0) return 0;

  let discount = type === "flat" ? value : (base * value) / 100;
  if (type === "percent" && settings.prepaidDiscountMax != null && num(settings.prepaidDiscountMax) > 0) {
    discount = Math.min(discount, num(settings.prepaidDiscountMax));
  }
  return round2(Math.min(discount, base));
};

// Partial COD: the part of a COD order paid online up front (the rest is cash on delivery). It is the larger
// of the high-risk tier (if on) and, when the admin turned "covers delivery" on, the delivery charge; it never
// swallows the whole order.
export const computeAdvance = (settings, total, paymentMethod, shippingFee = 0) => {
  if (paymentMethod !== "cod") return 0;

  let advance = 0;
  if (settings.codAdvanceEnabled && total >= num(settings.codAdvanceAbove)) {
    const high = settings.codAdvanceHighAbove != null && total >= num(settings.codAdvanceHighAbove);
    advance = num(high ? settings.codAdvanceHighAmount : settings.codAdvanceAmount);
  }
  if (settings.codAdvanceCoversDelivery) advance = Math.max(advance, num(shippingFee));
  return advance > 0 && advance < total ? round2(advance) : 0;
};

/**
 * Every figure of the order summary for one payment method.
 * @param subtotal goods total (all lines, options and quantities included)
 * @param couponDiscount the coupon discount already worked out for those goods
 */
export const computeCheckout = ({ settings, subtotal, couponDiscount = 0, paymentMethod }) => {
  const discount = round2(couponDiscount);
  const { shippingFee, codFee } = computeCharges(settings, subtotal, paymentMethod);
  const prepaidDiscount = computePrepaidDiscount(settings, subtotal - discount, paymentMethod);
  const total = round2(subtotal + shippingFee + codFee - discount - prepaidDiscount);
  const advance = computeAdvance(settings, total, paymentMethod, shippingFee);
  const freeShippingGap =
    settings.freeShippingAbove != null && shippingFee > 0 ? Math.max(round2(settings.freeShippingAbove - subtotal), 0) : 0;

  return {
    subtotal: round2(subtotal),
    shippingFee,
    codFee,
    discount,
    prepaidDiscount,
    total,
    advance,
    payOnDelivery: advance > 0 ? round2(total - advance) : 0,
    freeShippingGap,
  };
};

const day = (iso) => new Date(iso).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short" });

// "Delivery by 22 Oct – 24 Oct", or "Delivery by 22 Oct" when both ends fall on one day.
export const formatDeliveryWindow = (fromIso, toIso) => {
  if (!fromIso || !toIso) return "";
  const from = day(fromIso);
  const to = day(toIso);
  return from === to ? `Delivery by ${to}` : `Delivery by ${from} – ${to}`;
};
